"use client";

import React, { useState } from "react";
import { 
  ArrowRight, 
  Activity, 
  DollarSign, 
  ShieldCheck, 
  Trophy, 
  CheckCircle2, 
  Sparkles, 
  QrCode, 
  HeartPulse, 
  Calendar, 
  Users, 
  Zap,
  TrendingUp,
  Clock,
  Lock,
  ChevronRight
} from "lucide-react";

interface ModulesGridProps {
  onOpenDemo: () => void;
}

export function ModulesGrid({ onOpenDemo }: ModulesGridProps) {
  return (
    <section id="modules" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
        {/* Section Header */}
        <div className="space-y-4 max-w-3xl">
          <div className="inline-flex items-center gap-2 border border-[#1A3C2B]/20 bg-[#1A3C2B]/5 px-3 py-1 font-mono-tech text-[10px] uppercase tracking-widest text-[#1A3C2B]">
            <span>03. BENTO FEATURE MATRIX // 4 CORE ENGINES</span>
          </div>
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            4 CORE ENGINES. <br />
            SIMPLIFIED FOR EVERY SCHOOL &amp; PARENT.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            EVERYTHING A MODERN SCHOOL NEEDS: SCIENTIFIC TALENT DISCOVERY, NEW REVENUE FROM IDLE GROUNDS, 360° MEDICAL SAFETY, AND EFFORTLESS TOURNAMENTS.
          </p>
        </div>

        {/* Bento Feature Grid: 2x2 with clear visual infographics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* MODULE 01: TALENT DISCOVERY */}
          <div className="bg-white border-2 border-[#1A3C2B]/20 hover:border-[#1A3C2B] p-8 space-y-6 flex flex-col justify-between transition-all duration-300 shadow-sm relative group">
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest bg-[#FF8C69]/20 text-[#1A3C2B] px-2.5 py-1">
                    ENGINE_01 // TALENT RADAR
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-mono-tech text-[#1A3C2B] font-bold">
                  <Activity className="w-4 h-4 text-[#FF8C69]" />
                  <span>BIOMETRIC TALENT DISCOVERY</span>
                </div>
              </div>

              <div>
                <h3 className="font-space font-bold text-2xl text-[#1A3C2B] uppercase tracking-tight">
                  SCIENTIFIC TALENT RADAR &amp; GROWTH TIMING
                </h3>
                <p className="text-xs text-[#3A3A38] font-mono-tech mt-2 leading-relaxed">
                  Eliminate guesswork and favoritism. Scientifically calculates every student&apos;s natural athletic strengths and Peak Height Velocity (PHV) so late-blooming champions aren&apos;t cut early.
                </p>
              </div>

              {/* Infographic Explainer Box 1 */}
              <div className="bg-[#F7F7F5] border border-[#3A3A38]/20 p-5 space-y-4 font-mono-tech">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#1A3C2B] border-b border-[#3A3A38]/10 pb-2">
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#FF8C69]" />
                    LIVE STUDENT BIOMETRIC TELEMETRY
                  </span>
                  <span className="text-[#FF8C69] bg-white px-2 py-0.5 border border-[#3A3A38]/10 text-[10px]">
                    MIRWALD PHV ACTIVE
                  </span>
                </div>

                {/* Biological Age vs Calendar Age Bar */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[10px] text-[#3A3A38]">
                    <span>Chronological Age: <strong>13.2 Years</strong></span>
                    <span className="text-[#1A3C2B] font-bold">Biological Maturity: <strong>14.6 Years (+1.4y offset)</strong></span>
                  </div>
                  <div className="h-2.5 w-full bg-[#3A3A38]/10 rounded-full overflow-hidden flex">
                    <div className="h-full bg-[#1A3C2B]" style={{ width: "65%" }} />
                    <div className="h-full bg-[#FF8C69]" style={{ width: "20%" }} />
                  </div>
                  <div className="text-[10px] text-[#FF8C69] font-bold flex items-center gap-1">
                    <span>⚡ Status: Approaching Peak Growth Spurt (Maximize Flexibility &amp; Agility)</span>
                  </div>
                </div>

                {/* Eurofit Stations Mini Grid */}
                <div className="grid grid-cols-3 gap-2 pt-1 text-[11px]">
                  <div className="bg-white p-2 border border-[#3A3A38]/15 text-center">
                    <span className="text-[9px] text-[#3A3A38]/70 block">SPRINT LATENCY</span>
                    <strong className="text-sm text-[#1A3C2B]">98.4%</strong>
                    <span className="text-[9px] text-[#1A3C2B] block">National Top 2%</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15 text-center">
                    <span className="text-[9px] text-[#3A3A38]/70 block">VO2 MAX STAMINA</span>
                    <strong className="text-sm text-[#1A3C2B]">94.1%</strong>
                    <span className="text-[9px] text-[#1A3C2B] block">Elite Aerobic</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15 text-center">
                    <span className="text-[9px] text-[#3A3A38]/70 block">VERTICAL POWER</span>
                    <strong className="text-sm text-[#1A3C2B]">91.8%</strong>
                    <span className="text-[9px] text-[#1A3C2B] block">Fast-Twitch</span>
                  </div>
                </div>

                {/* AI Olympic Sport Recommendation */}
                <div className="bg-white p-2.5 border-l-4 border-[#1A3C2B] flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[9px] text-[#3A3A38]/70 uppercase block font-bold">AI Recommended Olympic Track</span>
                    <strong className="text-[#1A3C2B] text-xs">100m Sprint, Long Jump &amp; Badminton</strong>
                  </div>
                  <span className="bg-[#1A3C2B] text-white text-[10px] font-bold px-2 py-0.5">
                    HIGH AFFINITY
                  </span>
                </div>
              </div>

              {/* Layman Benefits List */}
              <div className="space-y-2 font-mono-tech text-xs text-[#3A3A38]">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For PE Teachers:</strong> No paper clipboards. Auto-grades class physical fitness in under 15 minutes.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Parents:</strong> Digital mobile report card showing your child&apos;s real stamina, speed, and growth curve.</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#3A3A38]/10 flex items-center justify-between">
              <span className="text-[10px] font-mono-tech text-[#3A3A38]/60 uppercase font-bold">
                STANDARDIZED EUROFIT &amp; MIRWALD PHV
              </span>
              <button 
                onClick={onOpenDemo}
                className="text-xs font-mono-tech font-bold text-[#1A3C2B] hover:underline flex items-center gap-1"
              >
                <span>View Sample Radar</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* MODULE 02: REVENUE GENERATION */}
          <div className="bg-white border-2 border-[#1A3C2B]/20 hover:border-[#1A3C2B] p-8 space-y-6 flex flex-col justify-between transition-all duration-300 shadow-sm relative group">
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest bg-[#9EFFBF]/40 text-[#1A3C2B] px-2.5 py-1">
                    ENGINE_02 // CAMPUS REVENUE
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-mono-tech text-[#1A3C2B] font-bold">
                  <DollarSign className="w-4 h-4 text-[#1A3C2B]" />
                  <span>FACILITY MONETIZATION</span>
                </div>
              </div>

              <div>
                <h3 className="font-space font-bold text-2xl text-[#1A3C2B] uppercase tracking-tight">
                  AFTER-HOURS FACILITY MONETIZATION ERP
                </h3>
                <p className="text-xs text-[#3A3A38] font-mono-tech mt-2 leading-relaxed">
                  Turn locked grounds into passive income. Safely opens synthetic turf, courts, and pools to verified community players from 4:00 PM to 10:00 PM and weekends.
                </p>
              </div>

              {/* Infographic Explainer Box 2 */}
              <div className="bg-[#F7F7F5] border border-[#3A3A38]/20 p-5 space-y-4 font-mono-tech">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#1A3C2B] border-b border-[#3A3A38]/10 pb-2">
                  <span className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-[#1A3C2B]" />
                    CAMPUS REVENUE LEDGER &amp; ACCESS CONTROL
                  </span>
                  <span className="text-[#1A3C2B] bg-[#9EFFBF]/50 px-2 py-0.5 text-[10px]">
                    AUTO-UPI SETTLED
                  </span>
                </div>

                {/* Monthly Revenue Visual Meter */}
                <div className="bg-white p-3 border border-[#3A3A38]/15 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] text-[#3A3A38]/70 block">MONTHLY PASSIVE YIELD</span>
                    <strong className="text-xl sm:text-2xl text-[#1A3C2B] font-space font-bold">₹4,85,000</strong>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-[#1A3C2B] bg-[#9EFFBF]/40 px-2 py-0.5 font-bold block mb-1">
                      +100% UTILIZATION
                    </span>
                    <span className="text-[9px] text-[#3A3A38]">18 Evening Slots / Wk</span>
                  </div>
                </div>

                {/* Step-by-Step Infographic Pipeline */}
                <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <Clock className="w-3.5 h-3.5 text-[#1A3C2B] mx-auto mb-1" />
                    <strong>1. Peak Slot</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Dynamic pricing based on floodlights</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <DollarSign className="w-3.5 h-3.5 text-[#1A3C2B] mx-auto mb-1" />
                    <strong>2. UPI Instant</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Funds flow direct to school bank</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <QrCode className="w-3.5 h-3.5 text-[#1A3C2B] mx-auto mb-1" />
                    <strong>3. QR Gate Pass</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Guard scans ticket at entry gate</span>
                  </div>
                </div>

                {/* Zero Liability Notice */}
                <div className="bg-white p-2 border-l-4 border-[#9EFFBF] flex items-center justify-between text-xs">
                  <span className="text-[10px] text-[#3A3A38]">
                    🔒 Pre-authorized damage security deposit held in escrow for every rental.
                  </span>
                </div>
              </div>

              {/* Layman Benefits List */}
              <div className="space-y-2 font-mono-tech text-xs text-[#3A3A38]">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For School Trustees:</strong> Transforms athletic grounds from an expense into a ₹36L–₹60L annual asset.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Campus Security:</strong> Zero strangers during school hours; verified players get time-locked QR gate passes.</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#3A3A38]/10 flex items-center justify-between">
              <span className="text-[10px] font-mono-tech text-[#3A3A38]/60 uppercase font-bold">
                100% AUTOMATED RAZORPAY / CASHFREE SETTLEMENT
              </span>
              <button 
                onClick={onOpenDemo}
                className="text-xs font-mono-tech font-bold text-[#1A3C2B] hover:underline flex items-center gap-1"
              >
                <span>Calculate Campus ROI</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* MODULE 03: HEALTH & MEDICAL SAFETY */}
          <div className="bg-white border-2 border-[#1A3C2B]/20 hover:border-[#1A3C2B] p-8 space-y-6 flex flex-col justify-between transition-all duration-300 shadow-sm relative group">
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest bg-[#F4D35E]/30 text-[#1A3C2B] px-2.5 py-1">
                    ENGINE_03 // SAFETY &amp; HEALTH
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-mono-tech text-[#1A3C2B] font-bold">
                  <HeartPulse className="w-4 h-4 text-rose-600" />
                  <span>360° ATHLETIC HEALTH</span>
                </div>
              </div>

              <div>
                <h3 className="font-space font-bold text-2xl text-[#1A3C2B] uppercase tracking-tight">
                  HEALTH, MEDICAL LOCKER &amp; INJURY PROTOCOLS
                </h3>
                <p className="text-xs text-[#3A3A38] font-mono-tech mt-2 leading-relaxed">
                  Protect every child on the field. Clinical AAP pre-participation screenings, mandatory concussion protocols, and instant trainer alerts keep school sports 100% safe.
                </p>
              </div>

              {/* Infographic Explainer Box 3 */}
              <div className="bg-[#F7F7F5] border border-[#3A3A38]/20 p-5 space-y-4 font-mono-tech">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#1A3C2B] border-b border-[#3A3A38]/10 pb-2">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    CLINICAL MEDICAL SAFETY STATUS
                  </span>
                  <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px]">
                    100% COMPLIANT
                  </span>
                </div>

                {/* 3 Medical Safety Badges */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px]">
                  <div className="bg-white p-2.5 border border-[#3A3A38]/15 text-center">
                    <HeartPulse className="w-4 h-4 text-rose-500 mx-auto mb-1" />
                    <strong>AAP PPE Screening</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5 text-[9px]">Cardiac &amp; asthma flags checked</span>
                  </div>
                  <div className="bg-white p-2.5 border border-[#3A3A38]/15 text-center">
                    <ShieldCheck className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
                    <strong>Concussion Gate</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5 text-[9px]">Graduated return-to-play steps</span>
                  </div>
                  <div className="bg-white p-2.5 border border-[#3A3A38]/15 text-center">
                    <Lock className="w-4 h-4 text-[#1A3C2B] mx-auto mb-1" />
                    <strong>Parent Consent</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5 text-[9px]">Encrypted DPDP 2023 vault</span>
                  </div>
                </div>

                {/* Instant Alert Visual Indicator */}
                <div className="bg-white p-3 border-l-4 border-rose-500 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[10px] text-rose-700 font-bold block uppercase">
                      🚨 Instant Emergency Dispatch
                    </span>
                    <span className="text-[11px] text-[#3A3A38]">
                      If injury occurs on field, instant SMS &amp; app alert reaches Parents &amp; Head Coach in &lt;200ms.
                    </span>
                  </div>
                </div>
              </div>

              {/* Layman Benefits List */}
              <div className="space-y-2 font-mono-tech text-xs text-[#3A3A38]">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Principals:</strong> Total legal protection with audited pre-participation medical forms.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Parents:</strong> Zero worry. You control health records and receive instant alerts if your child is hurt.</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#3A3A38]/10 flex items-center justify-between">
              <span className="text-[10px] font-mono-tech text-[#3A3A38]/60 uppercase font-bold">
                DPDP ACT 2023 SEC. 9 PARENTAL CONSENT VAULT
              </span>
              <button 
                onClick={onOpenDemo}
                className="text-xs font-mono-tech font-bold text-[#1A3C2B] hover:underline flex items-center gap-1"
              >
                <span>View Medical Gate</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* MODULE 04: COMPETITION & TOURNAMENT ERP */}
          <div className="bg-white border-2 border-[#1A3C2B]/20 hover:border-[#1A3C2B] p-8 space-y-6 flex flex-col justify-between transition-all duration-300 shadow-sm relative group">
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest bg-[#1A3C2B]/15 text-[#1A3C2B] px-2.5 py-1">
                    ENGINE_04 // COMPETITION OS
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-mono-tech text-[#1A3C2B] font-bold">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <span>TOURNAMENTS &amp; MEETS</span>
                </div>
              </div>

              <div>
                <h3 className="font-space font-bold text-2xl text-[#1A3C2B] uppercase tracking-tight">
                  ANNUAL SPORTS DAY &amp; TOURNAMENT BRACKETS
                </h3>
                <p className="text-xs text-[#3A3A38] font-mono-tech mt-2 leading-relaxed">
                  Cut 3 days of chaotic spreadsheet scheduling down to 1 click. Automated fixture brackets, live scoreboard streaming for parents, referee dispatch, and digital certificates.
                </p>
              </div>

              {/* Infographic Explainer Box 4 */}
              <div className="bg-[#F7F7F5] border border-[#3A3A38]/20 p-5 space-y-4 font-mono-tech">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#1A3C2B] border-b border-[#3A3A38]/10 pb-2">
                  <span className="flex items-center gap-1.5">
                    <Trophy className="w-3.5 h-3.5 text-amber-500" />
                    LIVE TOURNAMENT BRACKET &amp; SCOREBOARD
                  </span>
                  <span className="text-[#1A3C2B] bg-[#9EFFBF]/50 px-2 py-0.5 text-[10px]">
                    64 TEAMS SYNCED
                  </span>
                </div>

                {/* Bracket Tree Visual Diagram */}
                <div className="bg-white p-3 border border-[#3A3A38]/15 space-y-2">
                  <div className="flex items-center justify-between text-[10px] border-b border-[#3A3A38]/10 pb-1.5">
                    <span className="text-[#3A3A38]/70">U-16 INTER-HOUSE FOOTBALL FINAL</span>
                    <span className="text-emerald-600 font-bold animate-pulse">● LIVE (82&apos;)</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-[#1A3C2B]">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 bg-[#1A3C2B]" />
                      <span>TAGORE HOUSE</span>
                    </div>
                    <span className="font-space text-base">2</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-[#1A3C2B]">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 bg-[#FF8C69]" />
                      <span>SHIVAJI HOUSE</span>
                    </div>
                    <span className="font-space text-base">1</span>
                  </div>
                </div>

                {/* 3 Quick Features */}
                <div className="grid grid-cols-3 gap-2 text-center text-[10px]">
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <strong>1-Click Brackets</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Round robin or knockout</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <strong>Parent Live App</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Live scores on smartphone</span>
                  </div>
                  <div className="bg-white p-2 border border-[#3A3A38]/15">
                    <strong>QR Certificates</strong>
                    <span className="text-[#3A3A38]/70 block mt-0.5">Tamper-proof medals</span>
                  </div>
                </div>
              </div>

              {/* Layman Benefits List */}
              <div className="space-y-2 font-mono-tech text-xs text-[#3A3A38]">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Sports Directors:</strong> Run sports meets seamlessly with zero delays, paper certificates, or scoring errors.</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                  <span><strong>For Students:</strong> Verified digital athletic passport of every match and achievement to showcase for university admissions.</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#3A3A38]/10 flex items-center justify-between">
              <span className="text-[10px] font-mono-tech text-[#3A3A38]/60 uppercase font-bold">
                FIXTURES, REFEREES &amp; DIGITAL PASSPORT
              </span>
              <button 
                onClick={onOpenDemo}
                className="text-xs font-mono-tech font-bold text-[#1A3C2B] hover:underline flex items-center gap-1"
              >
                <span>Explore Bracket Engine</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

        </div>

        {/* Bottom CTA Bar */}
        <div className="border border-[#3A3A38]/30 bg-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4 font-mono-tech text-xs shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-[#1A3C2B]" />
            <span className="text-[#1A3C2B] font-bold uppercase tracking-wider">
              ALL 4 ENGINES CONNECTED IN ONE UNIFIED SCHOOL PORTAL.
            </span>
          </div>
          <button
            onClick={onOpenDemo}
            className="px-6 py-2.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition uppercase tracking-wider font-bold rounded-none flex items-center gap-2"
          >
            <span>REQUEST FULL CAMPUS WALKTHROUGH</span>
            <ArrowRight className="w-3.5 h-3.5 text-[#9EFFBF]" />
          </button>
        </div>
      </div>
    </section>
  );
}

